import React from 'react';
import without from 'lodash/without';

import {useTranslation} from 'common/i18n/hooks';
// eslint-disable-next-line import/no-cycle
import {MultiMetricsQueryBlock} from 'common/query-builder/query-block/multi-metrics-menu/query-block';
import {
  MenuType,
  MultiMetricsSectionItem,
  SavedMetricMenuItemValue,
} from 'common/query-builder/query-block/multi-metrics-menu/types';
import {useMetricEntries} from 'common/report/insights/contexts/metrics';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import type {
  ActiveMenu,
  RowProps,
} from 'iron/common/query-builder/query-entry-section/row-types/new-metric-entry/types/catalog';
import {ReportName} from 'common/report/constants';
import {findKeyByValue} from 'common/util/typescript';
import {reporter} from 'common/report/error-reporting';

/** defines which metric types are available for selection on a given report mode */
const RestrictedMetricTypesForReportName: Partial<Record<ReportName, BookmarkTypes.MetricType>> = {
  [ReportName.Funnels]: BookmarkTypes.MetricType.Funnel,
  [ReportName.Retention]: BookmarkTypes.MetricType.Retention,
};

type MetricSelectorProps = RowProps & {
  disableContextPanel?: boolean;
  activeMenuType?: ActiveMenu;
};

/** Just an A/A test as we validate feature flags and experiments.
 *
 * Safe to remove after feature flags launch. Just shout out in #project-experiment-reporting
 * to make sure if you aren't sure.
 */
function useAATest() {
  const hasCheckedVariant = React.useRef(false);

  if (hasCheckedVariant.current) {
    return;
  }

  mixpanel?.flags
    ?.get_variant_value(`ff_exp_aa_test`)
    ?.then((variant: string) => {
      // Just a trivial operation so we can check this A/A test is working.
      // eslint-disable-next-line no-console
      console.info(`MetricSelector A/A test variant: ${variant}`);
    })
    ?.catch((error: unknown) => {
      reporter.warn(`MetricSelector A/A test failed`, error);
    });
  hasCheckedVariant.current = true;
}

/**
 * Component for selecting a metric: saved formula, saved metric, cohorts, events, behaviors, etc...
 * This component directly modifies the bookmark by using the `useMetricEntries` hook
 */
export function MetricSelector(props: MetricSelectorProps) {
  const {t} = useTranslation();
  const {addNewMetricItem, selectMetricItem} = useMetricEntries();
  useAATest();

  const label = props.entry.name || t(`query-builder.metrics.new-definition`);

  const handleMetricSelect = (selectedItem: MultiMetricsSectionItem) => {
    selectMetricItem({
      index: props.index,
      parentMetricIndex: props.parentMetricIndex,
      selectedItem,
      dataGroupId: props.sectionProps.entries?.at(-1)?.dataGroupId || null,
    });
  };

  const handleMetricCreate = (menuType: MenuType) => {
    /**
     * @todo(metrics) a neat workflow addition here would be to try to preserve any event selection from existing metric state
     * for instance if you have a simple behavior with 3 events, if you create a funnel from this metric, retain those 3 events
     * as funnel steps
     */
    const matchedType = findKeyByValue(BookmarkTypes.MetricType, menuType);
    addNewMetricItem({
      index: props.index,
      parentMetricIndex: props.parentMetricIndex,
      type: matchedType ? BookmarkTypes.MetricType[matchedType] : null,
    });
  };

  const isFormulaMetric = props.entry.type === BookmarkTypes.TopLevelMetricType.NewFormulaEntry;

  /** filters out the available metric selections */
  let excludeTypes: BookmarkTypes.MetricType[] = [];
  if (props.parentMetricIndex !== undefined) {
    /** disables selecting metrics or formulas inside a formula definition **/
    excludeTypes = [BookmarkTypes.MetricType.Formula];
  } else if (props.sectionProps.reportName && props.sectionProps.reportName in RestrictedMetricTypesForReportName) {
    /** makes sure only funnel or retention metrics are available for selection in those report modes */
    excludeTypes = without(
      Object.values(BookmarkTypes.MetricType),
      RestrictedMetricTypesForReportName[props.sectionProps.reportName]!,
      BookmarkTypes.MetricType.SavedMetric,
    );
  }

  return (
    <MultiMetricsQueryBlock
      disableContextPanel={props.disableContextPanel}
      projectHasCohorts
      selected={{
        menuType: isFormulaMetric ? MenuType.Formula : MenuType.SavedMetric,
        label: ``,
        value: {
          id: props.entry.id ?? undefined,
          definition: {
            behavior: props.entry.behavior,
            measurement: props.entry.measurement,
            display: props.entry.display,
          },
          hasUnsavedChanges: props.entry.hasUnsavedChanges,
          name: label,
        } as SavedMetricMenuItemValue,
      }}
      contextPanelParentElement={props.contextPanelParentElement}
      excludedMetricsRowTypes={excludeTypes}
      // hideCreateMetric={
      //   props.sectionProps.reportName &&
      //   [ReportName.Funnels, ReportName.Retention].includes(props.sectionProps.reportName)
      // }
      allDataGroups={props.sectionProps.dataGroupsEnabled}
      dataGroupId={props.sectionProps.dataGroupId}
      hideEditControls={props.sectionProps.hideEditControls}
      queryOrigin={props.sectionProps.queryOrigin}
      showSessionEvent={props.sectionProps.showSessions}
      supportB2bCohorts={props.sectionProps.supportB2bCohorts}
      trackName={props.sectionProps.trackName}
      projectId={String(props.sectionProps.projectId)}
      workspaceId={String(props.sectionProps.workspaceId)}
      userId={Number(props.sectionProps.userId)}
      onSelect={handleMetricSelect}
      onCreate={(ev: {detail: {item: MenuType}}) => handleMetricCreate(ev.detail.item)}
      /** @todo(metrics) figure out tracking */
      onTrack={() => null}
      onClose={() => {
        props.setActiveMenu?.(null);
      }}
      onOpen={() => props.setActiveMenu?.({menu: props.activeMenuType ?? `metric-selector`})}
      replayPreviewRenderer={props.replayPreviewRenderer}
    />
  );
}
